import { useCallback, useEffect, useMemo, useState } from "react";
import type { WorkflowGuidanceCardConfig, WorkflowGuidanceSession } from "../workflowGuidance/types";

const DISMISSED_STORAGE_KEY = "libretto:workflow-guidance:dismissed:v1";

type UseWorkflowGuidanceArgs = {
    enabled: boolean;
    session: WorkflowGuidanceSession | null;
    projectTracks: any[];
    transcriptWords: any[];
    aiToolsView: string;
    editForClarityState: {
        isDetecting: boolean;
        hasRun: boolean;
        draft: any[];
    };
    onOpenEditForClarityReview: () => void;
    onOpenPropertiesClip: (clipId?: string | null) => void;
};

const normalize = (value: unknown): string => String(value ?? "").trim().toLowerCase();

const isBrowser = () =>
    typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const readDismissedBySession = (): Record<string, string[]> => {
    if (!isBrowser()) return {};
    try {
        const raw = window.sessionStorage.getItem(DISMISSED_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
};

const writeDismissedBySession = (value: Record<string, string[]>) => {
    if (!isBrowser()) return;
    try {
        window.sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(value));
    } catch {}
};

const isTranscriptionCompleted = (value: unknown): boolean =>
    normalize(value) === "completed";

const isTranscriptionTerminalWithoutTranscript = (value: unknown): boolean => {
    const state = normalize(value);
    return state === "failed" || state === "cancelled" || state === "silent" || state === "not_needed";
};

const getTrackEnhancedState = (track: any): string => normalize(track?.enhancedTrackState);

export const useWorkflowGuidance = ({
    enabled,
    session,
    projectTracks,
    transcriptWords,
    aiToolsView,
    editForClarityState,
    onOpenEditForClarityReview,
    onOpenPropertiesClip,
}: UseWorkflowGuidanceArgs): {
    activeCard: WorkflowGuidanceCardConfig | null;
    dismissCard: (cardId: string) => void;
} => {
    const sessionKey = String(session?.key || "").trim();
    const [dismissedCardIds, setDismissedCardIds] = useState<string[]>([]);

    useEffect(() => {
        if (!sessionKey) {
            setDismissedCardIds([]);
            return;
        }
        const dismissedBySession = readDismissedBySession();
        const nextIds = Array.isArray(dismissedBySession[sessionKey]) ? dismissedBySession[sessionKey] : [];
        setDismissedCardIds(nextIds);
    }, [sessionKey]);

    const dismissCard = useCallback((cardId: string) => {
        const safeCardId = String(cardId || "").trim();
        if (!safeCardId) return;
        setDismissedCardIds((prev) => {
            if (prev.includes(safeCardId)) return prev;
            const next = [...prev, safeCardId];
            if (sessionKey) {
                const dismissedBySession = readDismissedBySession();
                dismissedBySession[sessionKey] = next;
                writeDismissedBySession(dismissedBySession);
            }
            return next;
        });
    }, [sessionKey]);

    const currentTrack = useMemo(() => {
        if (!session) return null;
        const safeTrackId = String(session.trackId || "").trim();
        const safeAssetId = String(session.assetId || "").trim();

        if (safeTrackId) {
            const byTrackId = projectTracks.find((track: any) => {
                const trackId = String(track?.trackId || track?.id || "").trim();
                return trackId === safeTrackId;
            });
            if (byTrackId) return byTrackId;
        }

        if (!safeAssetId) return null;
        return projectTracks.find((track: any) => String(track?.assetId || "").trim() === safeAssetId) ?? null;
    }, [session, projectTracks]);

    const currentTrackId = useMemo(
        () => String(currentTrack?.trackId || currentTrack?.id || session?.trackId || "").trim(),
        [currentTrack, session?.trackId],
    );

    const hasTranscriptWords = useMemo(() => {
        if (!currentTrackId) return false;
        return transcriptWords.some(
            (word: any) => String(word?.sourceAssetId || "").trim() === currentTrackId,
        );
    }, [currentTrackId, transcriptWords]);

    const transcriptionPending = useMemo(() => {
        if (!currentTrack) return false;
        if (hasTranscriptWords) return false;
        const state = currentTrack?.transcriptionState;
        if (isTranscriptionTerminalWithoutTranscript(state)) return false;
        return !isTranscriptionCompleted(state);
    }, [currentTrack, hasTranscriptWords]);

    const hasClarityDraft = Array.isArray(editForClarityState.draft) && editForClarityState.draft.length > 0;
    const enhancementState = getTrackEnhancedState(currentTrack);

    const activeCard = useMemo<WorkflowGuidanceCardConfig | null>(() => {
        if (!enabled || !session) return null;

        const isDismissed = (cardId: string) => dismissedCardIds.includes(cardId);

        switch (session.workflow) {
            case "edit_by_transcript": {
                if (!transcriptionPending || isDismissed("edit_by_transcript_intro")) return null;
                return {
                    id: "edit_by_transcript_intro",
                    title: "Edit by text",
                    body: "Transcription is in progress. Once it is ready, you can cut by editing the transcript, or use AI Tools to remove filler words, shorten pauses, and clean up retakes.",
                    targetSelectors: [
                        "[data-tour='transcription-placeholder']",
                        "[data-tour='transcript-panel']",
                    ],
                    placement: "right-start",
                };
            }
            case "clean_up_recording": {
                if (!isDismissed("clean_up_recording_review")) {
                    if (hasClarityDraft) {
                        return {
                            id: "clean_up_recording_review",
                            title: "Cleanup suggestions ready",
                            body: "Review each suggested edit here, or apply them all at once. You stay in control of what gets changed.",
                            targetSelectors: [
                                "[data-tour='clarity-preview-panel']",
                                "[data-tour='clarity-submit']",
                                "[data-tour='ai-tools-panel']",
                                "[data-tour='sidebar-ai-tools-button']",
                            ],
                            placement: "left-start",
                            primaryAction: {
                                label: "Review suggestions",
                                onClick: () => {
                                    onOpenEditForClarityReview();
                                    dismissCard("clean_up_recording_review");
                                },
                            },
                        };
                    }

                    if (editForClarityState.isDetecting) {
                        return {
                            id: "clean_up_recording_review",
                            title: "Finding suggested edits",
                            body: "Edit for Clarity is reviewing the transcript now. When it finishes, you will be able to review each suggested cut before applying anything.",
                            targetSelectors: [
                                "[data-tour='clarity-submit']",
                                "[data-tour='ai-tools-panel']",
                                "[data-tour='sidebar-ai-tools-button']",
                            ],
                            placement: "left-start",
                        };
                    }
                }

                if (!transcriptionPending || isDismissed("clean_up_recording_preparing")) return null;
                return {
                    id: "clean_up_recording_preparing",
                    title: "Preparing cleanup suggestions",
                    body: "We are generating a transcript first. As soon as it is ready, Edit for Clarity will start finding filler words, pauses, retakes, and other likely cuts.",
                    targetSelectors: [
                        "[data-tour='ai-tools-panel']",
                        "[data-tour='sidebar-ai-tools-button']",
                    ],
                    placement: "right-start",
                };
            }
            case "enhance_audio": {
                if (enhancementState === "ready" && !isDismissed("enhance_audio_ready")) {
                    return {
                        id: "enhance_audio_ready",
                        title: "Enhanced audio ready",
                        body: "Your audio has been updated with Studio Sound. You can turn this on or off anytime to compare the original and enhanced versions.",
                        targetSelectors: [
                            "[data-tour='studio-sound-toggle']",
                            "[data-tour='properties-sidebar-panel']",
                            "[data-tour='sidebar-properties-button']",
                        ],
                        placement: "left-start",
                        primaryAction: {
                            label: "Compare audio",
                            onClick: () => {
                                onOpenPropertiesClip(session.clipId);
                                dismissCard("enhance_audio_ready");
                            },
                        },
                    };
                }

                if ((enhancementState === "queued" || enhancementState === "processing") && !isDismissed("enhance_audio_processing")) {
                    return {
                        id: "enhance_audio_processing",
                        title: "Enhancing audio",
                        body: "Studio Sound is processing your file now. You can play and scrub normally while the enhanced version is prepared.",
                        secondaryText: "Powered by Auphonic.",
                        targetSelectors: [
                            "[data-tour='studio-sound-toggle']",
                            "[data-tour='properties-sidebar-panel']",
                            "[data-tour='sidebar-properties-button']",
                        ],
                        placement: "left-start",
                    };
                }

                return null;
            }
            case "add_captions": {
                if (isDismissed("add_captions_intro")) return null;
                return {
                    id: "add_captions_intro",
                    title: "Choose a caption style",
                    body: "Once transcription is ready, pick a preset here to add captions. Edit text in the transcript, and adjust styling on the canvas.",
                    targetSelectors: [
                        "[data-tour='captions-panel']",
                        "[data-tour='sidebar-captions-button']",
                    ],
                    placement: "left-start",
                };
            }
            case "create_social_clips": {
                if (transcriptionPending && !isDismissed("create_social_clips_preparing")) {
                    return {
                        id: "create_social_clips_preparing",
                        title: "Preparing clips",
                        body: "We are generating a transcript first. As soon as it is ready, Create Clips will open so you can generate short, shareable moments from this recording.",
                        targetSelectors: [
                            "[data-tour='transcription-placeholder']",
                            "[data-tour='transcript-panel']",
                        ],
                        placement: "right-start",
                    };
                }

                if (aiToolsView !== "create_clips" || isDismissed("create_social_clips_ready")) {
                    return null;
                }

                return {
                    id: "create_social_clips_ready",
                    title: "Create your clips",
                    body: "Your transcript is ready. Choose how many clips you want, then submit to generate short, shareable cuts from this recording.",
                    targetSelectors: [
                        "[data-tour='create-clips-panel']",
                        "[data-tour='create-clips-submit']",
                        "[data-tour='sidebar-ai-tools-button']",
                    ],
                    placement: "left-start",
                };
            }
            default:
                return null;
        }
    }, [
        dismissCard,
        dismissedCardIds,
        editForClarityState.isDetecting,
        enabled,
        enhancementState,
        hasClarityDraft,
        aiToolsView,
        onOpenEditForClarityReview,
        onOpenPropertiesClip,
        session,
        transcriptionPending,
    ]);

    return {
        activeCard,
        dismissCard,
    };
};
